import React from 'react';

import {Trans} from '../../libs/shared/core/src/helpers/internationalization/react';
import {makeEnumRuntimeType} from '../../libs/shared/core/src/models/api/types/enumTypes';
import {ApplicationEntryPointTypesEnum} from '../models/api/applicationEntryPointModel';
import {
  LocalApplicationChannelTypeComposerTypesEnum,
  LocalApplicationChannelTypeContactTypesEnum,
} from '../models/api/localApplicationChannelTypeModel';
import {LocalApplicationModel} from '../models/api/localApplicationModel';
import {
  LocalApplicationServerAuthenticationSendAsEnum,
  LocalApplicationServerAuthenticationStrategiesEnum,
  LocalApplicationServerOauthAuthorizationMethodsEnum,
} from '../models/api/localApplicationServerModel';

/*
 * Constants.
 */

export enum LocalApplicationFeaturesEnum {
  CHANNEL = 'channel',
  COMPOSER_TOOLBAR = 'composer-toolbar',
  INBOX_SIDEPANEL = 'inbox-side-panel',
  WEBHOOK = 'webhook',
  CONNECTORS = 'connectors',
}
export const RLocalApplicationFeature = makeEnumRuntimeType(LocalApplicationFeaturesEnum);

export enum LocalApplicationReferenceTypesEnum {
  STRING = 'string',
  URL = 'url',
}
export const RLocalApplicationReferenceType = makeEnumRuntimeType(LocalApplicationReferenceTypesEnum);

const renderedAuthStrategyMap: Record<LocalApplicationServerAuthenticationStrategiesEnum, React.ReactNode> = {
  [LocalApplicationServerAuthenticationStrategiesEnum.NONE]: (
    <Trans id="local-application-none-auth-strategy">None</Trans>
  ),
  [LocalApplicationServerAuthenticationStrategiesEnum.API_KEY]: (
    <Trans id="local-application-basic-auth-strategy">API Key</Trans>
  ),
  [LocalApplicationServerAuthenticationStrategiesEnum.BASIC]: (
    <Trans id="local-application-basic-auth-strategy">Basic</Trans>
  ),
  [LocalApplicationServerAuthenticationStrategiesEnum.BEARER]: (
    <Trans id="local-application-bearer-auth-strategy">Bearer</Trans>
  ),
  [LocalApplicationServerAuthenticationStrategiesEnum.OAUTH2]: (
    <Trans id="local-application-oauth2-auth-strategy">OAuth2</Trans>
  ),
};

const renderedAuthenticationSendAsMap: Record<
  LocalApplicationServerAuthenticationSendAsEnum,
  React.ReactNode
> = {
  [LocalApplicationServerAuthenticationSendAsEnum.HEADER]: (
    <Trans id="local-application-authentication-send-as-header">HTTP Header</Trans>
  ),
  [LocalApplicationServerAuthenticationSendAsEnum.QUERY_PARAM]: (
    <Trans id="local-application-authentication-send-as-query-param">Query Parameter</Trans>
  ),
};

const renderedAuthorizationMethodsMap: Record<
  LocalApplicationServerOauthAuthorizationMethodsEnum,
  React.ReactNode
> = {
  [LocalApplicationServerOauthAuthorizationMethodsEnum.HEADER_AND_BODY]: (
    <Trans id="local-application-authentication-oauth-method-header-and-body">Header and body</Trans>
  ),
  [LocalApplicationServerOauthAuthorizationMethodsEnum.HEADER]: (
    <Trans id="local-application-authentication-oauth-method-header">Header only</Trans>
  ),
  [LocalApplicationServerOauthAuthorizationMethodsEnum.BODY]: (
    <Trans id="local-application-authentication-oauth-method-body">Body only</Trans>
  ),
};

const renderedChannelTypeContactsMap: Record<LocalApplicationChannelTypeContactTypesEnum, React.ReactNode> = {
  [LocalApplicationChannelTypeContactTypesEnum.CUSTOM]: (
    <Trans id="local-application-channel-type-contact-type-custom">Custom</Trans>
  ),
  [LocalApplicationChannelTypeContactTypesEnum.EMAIL]: (
    <Trans id="local-application-channel-type-contact-type-email">Email</Trans>
  ),
  [LocalApplicationChannelTypeContactTypesEnum.PHONE]: (
    <Trans id="local-application-channel-type-contact-type-phone">Phone</Trans>
  ),
};

const renderedChannelTypeComposerMap: Record<LocalApplicationChannelTypeComposerTypesEnum, React.ReactNode> =
  {
    [LocalApplicationChannelTypeComposerTypesEnum.BASIC]: (
      <Trans id="local-application-channel-type-composer-type-basic">Basic</Trans>
    ),
    [LocalApplicationChannelTypeComposerTypesEnum.ADVANCED]: (
      <Trans id="local-application-channel-type-composer-type-advanced">Advanced</Trans>
    ),
  };

/*
 * Helpers.
 */

export function computeIsLocalApplicationFeatureActive(
  feature: LocalApplicationFeaturesEnum,
  application: LocalApplicationModel,
) {
  switch (feature) {
    case LocalApplicationFeaturesEnum.INBOX_SIDEPANEL:
      return Boolean(
        application.entryPoints?.items.some(
          (e) => e.type === ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL,
        ),
      );
    case LocalApplicationFeaturesEnum.COMPOSER_TOOLBAR:
      return Boolean(
        application.entryPoints?.items.some(
          (e) => e.type === ApplicationEntryPointTypesEnum.COMPOSER_TOOLBAR,
        ),
      );
    case LocalApplicationFeaturesEnum.CHANNEL:
      return Boolean(application.channelType);
    case LocalApplicationFeaturesEnum.WEBHOOK:
      return Boolean(application.webhook);
    default:
      return false;
  }
}

export function renderLocalApplicationServerAuthenticationConfigStrategy(
  type: LocalApplicationServerAuthenticationStrategiesEnum,
) {
  return renderedAuthStrategyMap[type];
}

export function renderLocalApplicationServerAuthorizationMethod(
  method: LocalApplicationServerOauthAuthorizationMethodsEnum,
) {
  return renderedAuthorizationMethodsMap[method];
}

export function renderLocalApplicationServerAuthenticationSendAs(
  sendAs: LocalApplicationServerAuthenticationSendAsEnum,
) {
  return renderedAuthenticationSendAsMap[sendAs];
}

export function renderLocalApplicationChannelTypeContactType(
  type: LocalApplicationChannelTypeContactTypesEnum,
) {
  return renderedChannelTypeContactsMap[type];
}

export function renderLocalApplicationComposerTypeContactType(
  type: LocalApplicationChannelTypeComposerTypesEnum,
) {
  return renderedChannelTypeComposerMap[type];
}

export function getInstallationId(application: LocalApplicationModel): number {
  return parseInt(
    application.installationUrl.substring(application.installationUrl.lastIndexOf('/') + 1),
    10,
  );
}

export function computeRedirectUrl(localApplication: LocalApplicationModel, host: string) {
  return `${host}/accounts/oauth/applications/${localApplication.applicationUid}/callback`;
}

export function renderReferencePreviewerType(type: LocalApplicationReferenceTypesEnum) {
  switch (type) {
    case LocalApplicationReferenceTypesEnum.URL:
      return <Trans id="local-application-reference-previewer-type-url">URL</Trans>;
    case LocalApplicationReferenceTypesEnum.STRING:
      return <Trans id="local-application-reference-previewer-type-string">String</Trans>;
    default:
      return null;
  }
}
